import { ActionAutoConfirmCountdownProps } from './types';
import { Clock3 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const getAutoConfirmAtTimestamp = (autoConfirmAt: string): number | null => {
  const autoConfirmAtTimestamp = new Date(autoConfirmAt).getTime();

  return Number.isNaN(autoConfirmAtTimestamp) ? null : autoConfirmAtTimestamp;
};

const getRemainingSeconds = (autoConfirmAt: string): number => {
  const autoConfirmAtTimestamp = getAutoConfirmAtTimestamp(autoConfirmAt);

  if (autoConfirmAtTimestamp === null) return 0;

  return Math.max(0, Math.ceil((autoConfirmAtTimestamp - Date.now()) / 1000));
};

const getTimeParts = (totalSeconds: number) => {
  const normalizedSeconds = Math.max(0, totalSeconds);
  const hours = Math.floor(normalizedSeconds / 3600);
  const minutes = Math.floor((normalizedSeconds % 3600) / 60);
  const seconds = normalizedSeconds % 60;

  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };
};

const ActionAutoConfirmCountdown = (props: ActionAutoConfirmCountdownProps) => {
  const {
    autoConfirmAt,
    onAutoConfirmExpired
  } = props;

  const [remainingSeconds, setRemainingSeconds] = useState<number>(() => getRemainingSeconds(autoConfirmAt));
  const handledAutoConfirmAtRef = useRef<string | null>(null);

  useEffect(() => {
    setRemainingSeconds(getRemainingSeconds(autoConfirmAt));

    const intervalId = window.setInterval(() => {
      setRemainingSeconds(getRemainingSeconds(autoConfirmAt));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [autoConfirmAt]);

  useEffect(() => {
    const autoConfirmAtTimestamp = getAutoConfirmAtTimestamp(autoConfirmAt);

    if (!onAutoConfirmExpired) return;
    if (autoConfirmAtTimestamp === null) return;
    if (remainingSeconds > 0) return;
    if (handledAutoConfirmAtRef.current === autoConfirmAt) return;

    handledAutoConfirmAtRef.current = autoConfirmAt;
    onAutoConfirmExpired();
  }, [autoConfirmAt, onAutoConfirmExpired, remainingSeconds]);

  const { hours, minutes, seconds } = getTimeParts(remainingSeconds);

  return (
    <p className="flex items-center gap-1 py-1 pl-6 font-sans text-xs leading-4 text-info-foreground">
      <Clock3
        className="size-4 shrink-0"
        strokeWidth={1.75}
      />
      <span>Авто-подтверждение через</span>
      <span className="inline-block w-[2ch] text-right tabular-nums">{hours}</span>
      <span>ч</span>
      <span className="inline-block w-[2ch] text-right tabular-nums">{minutes}</span>
      <span>мин</span>
      <span className="inline-block w-[2ch] text-right tabular-nums">{seconds}</span>
      <span>сек</span>
    </p>
  );
};

export default ActionAutoConfirmCountdown;